"use client";

import { useTranslations } from "next-intl";
import * as React from "react";
import { Link } from "@/i18n/navigation";
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Text,
} from "@/ui";
import { useConsent } from "../store";
import { CONSENT_CATEGORIES, type ConsentCategory } from "../types";

interface CookiePreferencesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

/**
 * Cookie preferences dialog for detailed consent management.
 * Allows users to toggle individual cookie categories.
 */
export function CookiePreferences({ open, onOpenChange, className }: CookiePreferencesProps) {
  const t = useTranslations("cookie_consent");
  const { consent, isLoaded, acceptAll, rejectNonEssential, setCategory } = useConsent();
  const [localState, setLocalState] = React.useState<Record<ConsentCategory, boolean>>(
    consent.categories,
  );

  // Sync local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalState(consent.categories);
    }
  }, [open, consent.categories]);

  const handleSave = () => {
    // Apply all local changes
    for (const cat of CONSENT_CATEGORIES) {
      if (!cat.required) {
        setCategory(cat.id, localState[cat.id]);
      }
    }
    onOpenChange(false);
  };

  const handleAcceptAll = () => {
    acceptAll();
    setLocalState({
      necessary: true,
      analytics: true,
      marketing: true,
    });
    onOpenChange(false);
  };

  const handleRejectAll = () => {
    rejectNonEssential();
    setLocalState({
      necessary: true,
      analytics: false,
      marketing: false,
    });
    onOpenChange(false);
  };

  // Don't render until loaded
  if (!isLoaded) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className={cn("max-w-lg", className)}>
        <DialogHeader>
          <DialogTitle>{t("preferences_title")}</DialogTitle>
          <DialogDescription>{t("preferences_description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {CONSENT_CATEGORIES.map((category) => (
            <div
              key={category.id}
              className="flex items-start gap-3 rounded-lg border p-3"
            >
              <Checkbox
                id={`cookie-${category.id}`}
                checked={localState[category.id]}
                disabled={category.required}
                onCheckedChange={(checked) => {
                  if (!category.required) {
                    setLocalState((prev) => ({
                      ...prev,
                      [category.id]: checked === true,
                    }));
                  }
                }}
              />
              <div className="flex-1">
                <Label
                  htmlFor={`cookie-${category.id}`}
                  className="font-medium"
                >
                  {t(`category_${category.id}_label`)}
                  {category.required && (
                    <span className="ml-2 text-xs text-muted-foreground">({t("required")})</span>
                  )}
                </Label>
                <Text className="mt-1 text-sm text-foreground/75">
                  {t(`category_${category.id}_description`)}
                </Text>
              </div>
            </div>
          ))}
        </div>

        <Text className="text-sm">
          {t("learn_more")}{" "}
          <Link
            href="/privacy-policy"
            className="underline"
          >
            {t("privacy_link")}
          </Link>
        </Text>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleRejectAll}
            className="w-full sm:w-auto"
          >
            {t("reject_all")}
          </Button>
          <Button
            variant="outline"
            onClick={handleAcceptAll}
            className="w-full sm:w-auto"
          >
            {t("accept_all")}
          </Button>
          <Button
            onClick={handleSave}
            className="w-full sm:w-auto"
          >
            {t("save_preferences")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
